<script lang="ts">
import { defineComponent, PropType, computed } from 'vue';
import { IonRow, toastController } from '@ionic/vue';
import { AvailableTopping, MenuItem, Topping, Size, Recipe, RequiredTopping } from '@/models/menuModels';
import checklist from '@/components/menu/toppings/checklist.vue';
import { LineItem } from '@/models/cartModels';
import { CALZATO_TOPPING_LIMIT, PIZZA_TOPPING_LIMIT, BAKERY_PIZZA_TOPPING_LIMIT } from '@/constants';

export default defineComponent({
    props: {
        initialItem: { type: Object as PropType<MenuItem> },
        availableTopping: { type: Object as PropType<AvailableTopping> },
        twoSidedItem: { type: Object as PropType<LineItem> },
        flavor: { type: Object as PropType<Topping> },
        dressing: { type: Object as PropType<Topping> },
        toppings: { type: Array as PropType<Topping[]> },
        defaultToppings: { type: Array as PropType<Topping[]> },
        selectedSize: { type: Object as PropType<Size> },
        selectedRecipe: { type: Object as PropType<Recipe> },
        hideSplitOpts: Boolean,
        checkCount: Boolean,
        parentToppingCount: { type: Number, default: 0 },
        ignoreTopping: { type: Number, default: 0 },
        sauceCups: { type: Array as PropType<RequiredTopping[]> },
    },
    components: { IonRow, checklist },
    emits: ['update:flavor', 'update:dressing', 'update:toppings', 'update:defaultToppings', 'update:twoSidedItem', 'blockSubmit'],
    computed: {
        toppingCount() {
            return (
                this.parentToppingCount +
                //add currently selected toppings
                (this.toppings?.filter((c) => c.selected && !c.isDefault).reduce((total, topping) => (total += topping.multiplier), 0) ?? 0) +
                //add default toppings
                (this.initialItem?.isSalad ? 0 : this.defaultToppings?.filter((c) => c.selected).reduce((total, topping) => (total += topping.multiplier), 0) ?? 0) -
                this.ignoreTopping
            );
        },
    },
    methods: {
        async tooManyToppings(limit) {
            const toast = await toastController.create({
                message: `<div>${limit} toppings is the max. <br> Love the enthusiasm, but nobody wants a soggy pizza/calzato/etc. <br> FYI: extra toppings count as 2.</div>`,
                duration: 4000,
                cssClass: 'ion-text-center',
                color: 'primary',
                position: 'middle',
            });
            return toast.present();
        },
    },
    watch: {
        toppingCount(count) {
            let setLimit = PIZZA_TOPPING_LIMIT;
            if (this.initialItem?.isCalzato) {
                setLimit = CALZATO_TOPPING_LIMIT;
            } else if (this.initialItem?.isBakery) {
                setLimit = BAKERY_PIZZA_TOPPING_LIMIT;
            }
            const limit = setLimit;
            if (this.checkCount && count > limit) this.tooManyToppings(limit);
            this.$emit('blockSubmit', count > limit);
        },
    },
});

export function passThroughBindings(props, emit) {
    const localFlavor = computed({
        get: () => props.flavor,
        set: (newValue) => {
            if (newValue) newValue.selected = true;
            emit('update:flavor', newValue);
        },
    });
    const localDressing = computed({
        get: () => props.dressing,
        set: (newValue) => emit('update:dressing', newValue),
    });
    const localToppings = computed({
        get: () => props.toppings,
        set: (newValue) => emit('update:toppings', newValue),
    });
    const localDefaultToppings = computed({
        get: () => props.defaultToppings,
        set: (newValue) => emit('update:defaultToppings', newValue),
    });
    const localTwoSidedItem = computed({
        get: () => props.twoSidedItem,
        set: (newValue) => emit('update:twoSidedItem', newValue),
    });

    return {
        localFlavor,
        localDressing,
        localToppings,
        localDefaultToppings,
        localTwoSidedItem,
    };
}
</script>
